import request from '@/plugins/admin_axios'
import { Notification } from '@/plugins/notifications'
import { loginApi, LogoutApi } from '@/services/backend/authentication'
import { defineStore } from 'pinia'
import { reactive, watch } from 'vue'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { websocketManager } from '@/composables/WebSocketManager';
import { eventActionBus } from '@/composables/EventActionBus'
import { ListAtendimentosEmAndamentoApi } from '@/services/backend/atendimento'

export const useAuthAdminStore = defineStore('authAdmin', () => {
  const router = useRouter()

  const user = ref(null)
  const token = ref(null)
  const notifications = reactive([])
  const totalNotificationUnread = ref(0)
  const isLoadingCountNotificationUnread = ref(false)
  const isLoadingNotifications = ref(false)
  const isLoadingReadNotification = ref(false)
  const setViewNotification = ref(2) // 1 Todas | 2 Não lidas | 3 Lidas
  const offset = ref(0)
  const limit = ref(10)
  const emptyNotifications = ref(false)
  const pendingAttendments = ref([])
  const TotalPendingAttendments = computed(() => pendingAttendments.value.length)

  const isAuthenticated = computed(() => user.value !== null || token.value !== null)
  const isCustomer = computed(() => isAuthenticated.value && user.value.is_customer)
  const TotalNotifications = computed(() => {
    return notificationsFilter.value.length
  })
  const notificationsFilter = computed(() => {

    switch (setViewNotification.value) {
      case 1:
        return notifications

      case 2:
        return notifications.filter(n => !n.is_read)

      case 3:
        return notifications.filter(n => n.is_read)
    }

    return []
  })

  /**
   * @type {ComputedRef<string[]>}
   */
  const perfis_uuid = computed(() => {
    return user.value?.perfis.map(p => p.uuid)
  })

  const hasPermission = (modulo = undefined, submodulo = undefined, permissao = undefined) => {
    if (!user.value) return false
    if (user.value.is_master) return true
    return (
      user.value?.permissoes.some((p) => {
        return (
          (!modulo || p.modulo === modulo) &&
          (!submodulo || p.submodulo === submodulo) &&
          (!permissao || p.permissao === permissao)
        )
      }) ?? false
    )
  }

  const synchronizeUserPermissions = async () => {
    if (isAuthenticated.value && !isCustomer.value) {
      try {
        const response = await request.get(`user/get/?uuid=${user.value.uuid}`)
        if (response.status === 200) {
          user.value.perfis = response.data.data.perfis
          user.value.permissoes = response.data.data.permissoes
        }
      } catch (error) {
        console.error('Erro ao sincronizar permissões do usuário:', error)
      }
    }
  }

  const login = async (formdata) => {
    const response = await loginApi(formdata)
    if (response.status == 'success') {
      user.value = response.data.user
      token.value = response.data.token
      await GetAllNotifications()
    }
  }

  const logout = async () => {

    if (isAuthenticated.value) {
      await LogoutApi()
    }
    ClearSession()
  }

  const ClearSessionData = () => {
    user.value = null
    token.value = null
  }

  const ClearSession = () => {
    ClearSessionData()
    router.push('/admin/login')
  }


  const ReadNotification = async (uuid, loading = false) => {

    let notification = notifications.find(n => n.uuid === uuid && n.is_read)
    if (notification) {
      return
    }

    try {
      if (loading) {
        isLoadingReadNotification.value = true
      }
      const response = await request.put(`/notifications/read/${uuid}`)
      if (response.status == 200) {
        notifications.find(n => n.uuid === uuid).is_read = true
      }
    } catch (error) {
      console.log(error);
      Notification.error('Não foi possível atualizar as notificações.')
    } finally {
      if (loading) {
        isLoadingReadNotification.value = false
      }
      await GetTotalUnreadNotifications()
    }
  }

  const ReadAllNotifications = async (loading = false) => {
    try {
      if (loading) {
        isLoadingReadNotification.value = true
      }
      const response  = await request.put('/notifications/read/')
      if (response.status == 200) {
        await GetAllNotifications()
      }
    } catch (error) {
      console.log(error);
      Notification.error('Não foi possível atualizar as notificações.')
    } finally {
      if (loading) {
        isLoadingReadNotification.value = false
      }
    }
  }

  const getFilter = () => {
    switch (setViewNotification.value) {
      case 1:
        return 'all'

      case 2:
        return 'not_read'

      case 3:
        return 'read'
    }
  }

  const GetAllNotifications = async (loading = false) => {
    try {
      if (emptyNotifications.value) return

      if (loading) isLoadingNotifications.value = true

      const response = await request.get("/notifications/list/", {
        params: { offset: offset.value, limit: limit.value, filter: getFilter()}
      })

      if (response.status == 200) {
        const newData = response.data.data || []

        if (newData.length) {
          notifications.push(...newData)
          offset.value += newData.length
        } else {
          emptyNotifications.value = true
        }
      }
    } catch (error) {
      const status = error?.response?.status || error?.status

      if (status != 404) {
        console.log(error);
        Notification.error('Não foi possível buscar as notificações.')
      }

      if (status == 404) {
        emptyNotifications.value = true
      }
    } finally {
      if (loading) isLoadingNotifications.value = false
      await GetTotalUnreadNotifications()
    }
  }

  const GetTotalUnreadNotifications = async () => {
    try {
      isLoadingCountNotificationUnread.value = true
      const response = await request.get('/notifications/unread_count/')
      if (response.status == 200) {
        totalNotificationUnread.value = response.data.data.total || 0
      }
    } catch (error) {
      console.error('Erro ao buscar notificações não lidas:', error)
      totalNotificationUnread.value = 0
    } finally {
      isLoadingCountNotificationUnread.value = false
    }
  }

  const GetLastNotifications = async () => {
    try {
      const response = await request.get('/notifications/list/', {
        params: { offset: 0, limit: 1 }
      })

      if (response.status == 200 && response.data.data?.length > 0) {
        notifications.unshift(...response.data.data)
        offset.value++
        return response.data.data[0]
      }
      return null
    } catch (error) {
      console.log(error);
      Notification.error('Não foi possível buscar as notificações.')
      return null
    } finally {
      await GetTotalUnreadNotifications()
    }
  }

  const InitWebsocketPerfis = () => {
    if (perfis_uuid.value && perfis_uuid.value.length > 0) {

      const ws_actions = {
        logout: logout,
        unplug_user: logout,
        update_user_perfil: synchronizeUserPermissions,
      }

      perfis_uuid.value.forEach(perfil_uuid => {
        websocketManager.connect(`user_perfil_${perfil_uuid}`, `ws/user_perfil/${perfil_uuid}/`)
        eventActionBus.on(`user_perfil_${perfil_uuid}`, (action, payload) => {
          if (ws_actions[action]) {
            ws_actions[action](payload);
          }
          return true
        })

      })

      const ws_actions_owner_notifications = {
        unplug_user: logout,
      }

      websocketManager.connect('owner_notifications', 'ws/notifications/')
      eventActionBus.on('owner_notifications', (actionName, payload) => {
        console.log(`[AuthAdminStore] Evento recebido do bus: ${actionName}`);
        if (ws_actions_owner_notifications[actionName]) {
          ws_actions_owner_notifications[actionName](payload);
        }
        return true
      })
    }
  }

  const FinishWebsocketPerfis = () => {
    if (perfis_uuid.value && perfis_uuid.value.length > 0) {
      perfis_uuid.value.forEach(perfil_uuid => {
        websocketManager.disconnect(`user_perfil_${perfil_uuid}`)
        eventActionBus.clear(`user_perfil_${perfil_uuid}`)
      })
    }

    websocketManager.disconnect('owner_notifications')
    eventActionBus.clear('owner_notifications')
  }
  const GetPendingAttendments = async () => {
    await new Promise(resolve => setTimeout(resolve, 500))

    try {
      const response = await ListAtendimentosEmAndamentoApi()
      pendingAttendments.value = response.data || []
      return pendingAttendments.value
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Erro ao buscar atendimentos em andamento:', error)
      }
      pendingAttendments.value = []
    }
    return pendingAttendments.value
  }

  const ClearNotifications = () => {
    notifications.splice(0, notifications.length)
    offset.value = 0
    emptyNotifications.value = false
  }

  watch(perfis_uuid, (newValues) => {
    if (newValues && newValues.length > 0) {
      InitWebsocketPerfis()
    }
  })

  return {
    user,
    token,
    notifications,
    notificationsFilter,
    TotalNotifications,
    totalNotificationUnread,
    pendingAttendments,
    TotalPendingAttendments,
    isLoadingNotifications,
    isLoadingReadNotification,
    isLoadingCountNotificationUnread,
    isAuthenticated,
    isCustomer,
    setViewNotification,
    emptyNotifications,
    offset,
    limit,
    hasPermission,
    login,
    logout,
    synchronizeUserPermissions,
    ReadNotification,
    ReadAllNotifications,
    GetAllNotifications,
    GetLastNotifications,
    GetPendingAttendments,
    InitWebsocketPerfis,
    FinishWebsocketPerfis,
    ClearSession,
    ClearSessionData,
    ClearNotifications,
  }
}, { persist: {
  pick: ['user', 'token']
} })
